import React, { useCallback } from "react";

import { PREVIEW_ICON_SIZE, PreviewIcon } from "App/common/PreviewIcon";
import { ReactComponent as ArrowUpRightSVG } from "App/common/icons/arrow-up-right.svg";
import { Ad } from "./AdModel";
import { useAdsContext } from "./AdsContext";
import {
  AdTrackerParams,
  trackAdClick,
  useAdImpressionTracker,
} from "./useAdImpressionTracker";
import "./PinnedAdTile.scss";

export type PinnedAdTileProps = {
  ad: Ad;
  /**
   * Optional click override. Defaults to opening `ad.linkUrl` in a new
   * tab. Tests use this seam to assert behavior without invoking
   * `window.open`.
   */
  onClick?: (ad: Ad) => void;
  /**
   * Analytics injection seam — same semantics as on `CarouselAdCard`.
   */
  track?: AdTrackerParams["track"];
  /**
   * Optional override for the spaceId threaded into analytics. When
   * omitted, the value is read from `AdsContext`.
   */
  spaceId?: string | null;
};

const SURFACE = "space_pinned" as const;

const openAdLink = (ad: Ad) => {
  if (!ad.linkUrl) return;
  // Match CarouselAdCard: open in a new tab with noopener/noreferrer so
  // referrer isn't leaked and the opened tab cannot access ours via
  // `window.opener`.
  window.open(ad.linkUrl, "_blank", "noopener,noreferrer");
};

/**
 * Derives the subtitle shown beneath the ad title — the bare hostname of
 * `ad.linkUrl` (stripped of a leading `www.`). Falls back to
 * `ad.advertiserName` when the link url is unparseable or has no host,
 * so the tile never renders an empty subtitle.
 */
const subtitleFromLinkUrl = (ad: Ad): string => {
  if (!ad.linkUrl) return ad.advertiserName;
  try {
    const host = new URL(ad.linkUrl).hostname;
    return host.replace(/^www\./, "") || ad.advertiserName;
  } catch {
    return ad.advertiserName;
  }
};

/**
 * A single pinned-ad tile rendered above the space's items list.
 *
 * Layout is a self-contained row: a LARGE `PreviewIcon` (same size +
 * variants as a real EntityTable track row uses) plus a title and
 * subtitle stack. Typography + spacing match `ThumbnailPreview.scss`
 * (`.highnote-thumbnail-preview .info .title` / `.description`).
 *
 * The tile is not wrapped in any BaseTable / EntityTable class
 * cascade so it does not participate in sibling selectors targeting
 * the real `EntityTable` underneath it.
 *
 * Impressions fire once when the tile first becomes visible (via
 * `IntersectionObserver`) on the `space_pinned` surface. Clicks fire
 * alongside opening the link so we always record the interaction
 * before the browser loses focus.
 */
export const PinnedAdTile = ({
  ad,
  onClick,
  track,
  spaceId,
}: PinnedAdTileProps) => {
  const ctxSpaceId = useAdsContext().spaceId;
  const effectiveSpaceId = spaceId !== undefined ? spaceId : ctxSpaceId;
  const nodeRef = useAdImpressionTracker({
    ad,
    spaceId: effectiveSpaceId,
    surface: SURFACE,
    track,
  });

  // Title preference: prefer explicit `title`, fall back to legacy
  // `text`. Matches the Flutter pinned-ad widget, which uses the same
  // fallback chain for pre-migration ads that still lack a dedicated
  // title.
  const title = ad.title ?? ad.text;
  const subtitle = subtitleFromLinkUrl(ad);

  const handle = useCallback(() => {
    trackAdClick(ad, effectiveSpaceId, SURFACE, track);
    if (onClick) {
      onClick(ad);
      return;
    }
    openAdLink(ad);
  }, [ad, onClick, effectiveSpaceId, track]);

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handle();
    }
  };

  return (
    <div
      ref={(el) => {
        nodeRef.current = el;
      }}
      className="highnote-pinned-ad-tile"
      data-testid="pinned-ad-tile"
      data-ad-id={ad.id}
      data-ad-type={ad.adType}
      role="button"
      tabIndex={0}
      onClick={handle}
      onKeyDown={handleKey}
    >
      {/*
        Leading spacer that mirrors the EntityTable row's pre-thumbnail
        gutter (drag-handle slot + expand chevron slot + the gaps
        between them) so the ad's thumbnail aligns with the track
        thumbnails sitting beneath the list. Sized in SCSS to match the
        widths declared in `BaseTable.scss` / `BaseTableV2.tsx` —
        kept out of the React tree so we don't reintroduce the
        BaseTable/EntityTable class cascade that the tile deliberately
        avoids.
      */}
      <div className="highnote-pinned-ad-tile__lead-spacer" aria-hidden />
      <div className="highnote-pinned-ad-tile__thumb">
        <PreviewIcon
          size={PREVIEW_ICON_SIZE.LARGE}
          src={ad.imageUrl || undefined}
          fallback={
            <div
              className="highnote-pinned-ad-tile__thumb-fallback"
              aria-hidden
            />
          }
        />
      </div>
      <div className="highnote-pinned-ad-tile__info">
        {title ? (
          <span
            className="highnote-pinned-ad-tile__title"
            data-is-clickable="false"
          >
            {title}
          </span>
        ) : null}
        {subtitle ? (
          <span className="highnote-pinned-ad-tile__subtitle">
            <ArrowUpRightSVG
              className="highnote-pinned-ad-tile__subtitle-icon"
              aria-hidden
              focusable={false}
            />
            <span className="highnote-pinned-ad-tile__subtitle-text">
              {subtitle}
            </span>
          </span>
        ) : null}
      </div>
    </div>
  );
};
